<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { getDisplayInfo } from '@/composable/useToolsFareOptimizer'
import { IEnergyInvoice } from '@/interfaces/entities/energyInvoice'

const props = defineProps<{ invoiceId: number }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const store = useFareOptimizerStore()
const invoice = ref<IEnergyInvoice | null>(null)

// Rótulos pt-BR de todos os campos da página 2 (histórico de 13 meses).
const HISTORICAL_FIELD_LABELS: Record<string, string> = {
  peak_invoiced_consumption: 'Consumo Ponta (kWh)',
  off_peak_invoiced_consumption: 'Consumo Fora Ponta (kWh)',
  intermediate_consumption: 'Consumo Intermediário (kWh)',
  peak_measured_demand: 'Demanda Ponta (kW)',
  off_peak_measured_demand: 'Demanda Fora Ponta (kW)',
  peak_reactive_energy: 'Reativo Ponta (kVArh)',
  off_peak_reactive_energy: 'Reativo Fora Ponta (kVArh)',
  peak_reactive_demand: 'Demanda Reativa Ponta (kVAr)',
  off_peak_reactive_demand: 'Demanda Reativa Fora Ponta (kVAr)',
  reserved_consumption: 'Consumo Reservado (kWh)',
  reserved_reactive_energy: 'Reativo Reservado (kVArh)',
  tusdg_measured_demand: 'Demanda Medida TUSDG (kW)',
}

async function load(id: number) {
  invoice.value = await store.fetchInvoiceDetail(id)
}

onMounted(() => load(props.invoiceId))
watch(() => props.invoiceId, load)

const monthLabel = computed(() => {
  if (!invoice.value) return ''
  const [year, month] = invoice.value.reference_month.split('-')
  return `${month}/${year}`
})

// Itens detalhados (read-only) só existem em faturas completas.
const items = computed(() => {
  const fareItems = invoice.value?.fare_items
  if (!fareItems) return []
  return Object.entries(fareItems)
    .filter(([key, item]) => key !== 'tariff_total' && item && (item.amount !== 0 || item.price_per_unit !== 0 || item.value !== 0))
    .map(([key, item]) => {
      const { displayName, unit } = getDisplayInfo(key)
      return { key, displayName: displayName + (unit ? ` - ${unit}` : ''), ...item }
    })
})

const brl = (value: number | null | undefined) => useCurrencyFormaterFromCents(value ?? 0)

// Snapshot completo da página 2 (registros parciais/backfill): mostra TODOS os
// campos salvos, não só consumo. Ignora o rótulo do mês e valores vazios.
const snapshotEntries = computed(() => {
  const raw = invoice.value?.raw_extraction
  if (!raw) return []
  return Object.entries(raw)
    .filter(([key, value]) => key !== 'month' && value != null && value !== '' && Number(value) !== 0)
    .map(([key, value]) => ({
      label: HISTORICAL_FIELD_LABELS[key] ?? key,
      value: Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }))
})
</script>

<template>
  <div class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('close')" />
    <div class="relative flex max-h-[90vh] w-full max-w-2xl flex-col bg-white p-6 shadow-xl">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-slate-900">{{ t('contract_optimizer.invoice_history.detail_title') }} · {{ monthLabel }}</h3>
        <button class="text-slate-400 hover:text-slate-700" @click="emit('close')">✕</button>
      </div>

      <div v-if="invoice" class="mt-4 overflow-y-auto">
        <!-- Registro parcial (backfill): mostra TODOS os campos da página 2. -->
        <template v-if="!invoice.complete">
          <p class="mb-4 bg-amber-100 px-4 py-3 text-sm text-amber-800">
            {{ t('contract_optimizer.invoice_history.partial_banner') }}
          </p>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div v-for="entry in snapshotEntries" :key="entry.label" class="border border-stroke px-3 py-2">
              <p class="text-xs uppercase text-slate-400">{{ entry.label }}</p>
              <p class="font-semibold text-slate-800">{{ entry.value }}</p>
            </div>
          </div>
        </template>

        <!-- Fatura cheia: resumo de consumo/demanda/custo + itens detalhados. -->
        <div v-else class="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div class="border border-stroke px-3 py-2">
            <p class="text-xs uppercase text-slate-400">{{ t('contract_optimizer.invoice_history.consumption') }}</p>
            <p class="font-semibold text-slate-800">{{ invoice.consumo_total?.toLocaleString('pt-BR') ?? '—' }} kWh</p>
          </div>
          <div class="border border-stroke px-3 py-2">
            <p class="text-xs uppercase text-slate-400">{{ t('contract_optimizer.invoice_history.demand') }}</p>
            <p class="font-semibold text-slate-800">{{ invoice.demanda_ponta_medida?.toLocaleString('pt-BR') ?? '—' }} kW</p>
          </div>
          <div class="border border-stroke px-3 py-2">
            <p class="text-xs uppercase text-slate-400">{{ t('contract_optimizer.invoice_history.cost') }}</p>
            <p class="font-semibold text-slate-800">{{ invoice.custo_total == null ? '—' : brl(invoice.custo_total) }}</p>
          </div>
        </div>

        <table v-if="items.length" class="mt-5 w-full text-sm">
          <thead>
            <tr class="border-b-2 border-stroke text-left text-xs uppercase tracking-wide text-slate-400">
              <th class="px-3 py-2">{{ t('contract_optimizer.invoice.name') }}</th>
              <th class="px-3 py-2 text-right">{{ t('contract_optimizer.invoice.amount') }}</th>
              <th class="px-3 py-2 text-right">{{ t('contract_optimizer.invoice.unit_price') }}</th>
              <th class="px-3 py-2 text-right">{{ t('contract_optimizer.invoice.value') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.key" class="border-b border-stroke">
              <td class="px-3 py-2 text-slate-700">{{ item.displayName }}</td>
              <td class="px-3 py-2 text-right">{{ item.amount.toLocaleString('pt-BR') }}</td>
              <td class="px-3 py-2 text-right">{{ brl(item.price_per_unit) }}</td>
              <td class="px-3 py-2 text-right">{{ brl(item.value) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
